import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/** How much of a page a header checkbox covers. */
export type SelectionState = 'none' | 'some' | 'all';

/**
 * A row / select-all checkbox with a real indeterminate state.
 *
 * The shadcn `Checkbox` is a Radix root whose indicator renders a tick for the
 * `indeterminate` state as well as `checked`, so a half-selected page would
 * claim to be a fully selected one. `components/ui` is shadcn-managed and must
 * not be edited (G9), so the tick is hidden and a dash drawn over it from here.
 *
 * `onChange` reports the state the user asked for, not a raw boolean: clicking
 * a half-selected header means "select the rest", never "clear what I have".
 */
export function SelectionCheckbox({
  state,
  onChange,
  label,
  disabled,
  className,
}: {
  state: SelectionState;
  onChange: (next: 'all' | 'none') => void;
  /** Names the row this selects, for screen readers. Required — an unlabelled
   *  checkbox column is a column of identical unnamed controls. */
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  const indeterminate = state === 'some';
  return (
    <span className="relative inline-flex">
      <Checkbox
        checked={state === 'all' ? true : indeterminate ? 'indeterminate' : false}
        onCheckedChange={() => onChange(state === 'all' ? 'none' : 'all')}
        aria-label={label}
        disabled={disabled}
        className={cn(
          indeterminate &&
            'border-primary bg-primary text-primary-foreground [&_svg]:invisible dark:bg-primary',
          className,
        )}
      />
      {indeterminate ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 grid place-content-center"
        >
          <span className="h-0.5 w-2 rounded-full bg-primary-foreground" />
        </span>
      ) : null}
    </span>
  );
}
